import React, { useContext, useEffect, useMemo } from 'react';
import {
	ApSimpleDropdown,
	ApCheckbox,
	ApButtonIcon,
	ApMenu,
	ApMenuItem,
} from '@alixpartners/ui-components';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { ChartOptionsCollapsible } from '../ChartOptionsCollapsible';
import {
	CHART_TYPE_OPTIONS,
	BREAKDOWN_BY,
	CHART_TYPE_OPTIONS_WITHOUT_BREAKDOWN,
	getBreakdownByOptions,
	getSpendByOptions,
	SPEND_BY_PROPS,
} from '../utils';
import {
	Wrapper,
	Divider,
	LeftWrapper,
	RightWrapper,
	Label,
	CheckboxWrapper,
} from './ChartOptions.styles';
import { SettingsContext } from 'contexts/SettingsContext';
import DownloadDeckPopup from 'Investigate/DownloadDeckPopup/DownloadDeckPopup';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';

const DEFAULT_OPTION = {
	label: '',
	value: '',
};
const EXPORT_OPTIONS = [
	{
		label: 'Export chart',
		value: 'png',
	},
	{
		label: 'Export table',
		value: 'xlsx',
	},
];

export const ChartOptions = ({
	spendBy,
	splitSpendByDimension,
	includeTailSpend,
	setQueryStringStateAndPush,
	breadcrumbs,
	onChartTypeChange,
	chartType,
	isDownloadDeckModalOpen,
	hideBreakdown,
	handleHighchartsExportToPng,
	handleExportToXls,
	isTailSpendOptions,
	handleDownloadDeck,
	handleIndustries,
	handleIndustryModal,
	// isDeckDownloading,
	spendType,
	permissions,
}) => {
	// If Breakdown - None is selected chart type options should be obly 2
	const { customFilters } = useContext(SettingsContext);
	const history = useHistory();
	useEffect(
		() => {
			if (splitSpendByDimension) {
				const chartTypeOptions = getChartTypeOptions();

				if (!chartTypeOptions.includes(chartType)) {
					onChartTypeChange(chartTypeOptions[0]);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[splitSpendByDimension]
	);

	useEffect(
		() => {
			if (!isTailSpendOptions && spendBy === SPEND_BY_PROPS.TAIL_SEGMENT) {
				setQueryStringStateAndPush({ spendBy: SPEND_BY_PROPS.CATEGORY });
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isTailSpendOptions]
	);
	const spendByOptions = getSpendByOptions(isTailSpendOptions);
	const breakdownByOptions = getBreakdownByOptions(
		isTailSpendOptions,
		customFilters
	);

	const handleSpendByChange = option => {
		setQueryStringStateAndPush({
			spendBy: option.value,
			hierarchy: [],
			selectedItem: undefined,
		});
	};

	const handleBreakdownByChange = option => {
		setQueryStringStateAndPush({ splitSpendByDimension: option.value });
	};

	const handleIncludeTailSpendChange = e => {
		const checked = e.target.checked;

		setQueryStringStateAndPush({ includeTailSpend: checked || undefined });
	};

	const getSelectedSpendBy = () => {
		return (
			spendByOptions.find(item => item.value === spendBy) || DEFAULT_OPTION
		);
	};

	const getSelectedBreakdownBy = () => {
		return (
			breakdownByOptions.find(item => item.value === splitSpendByDimension) ||
			DEFAULT_OPTION
		);
	};

	const getChartTypeOptions = () => {
		return splitSpendByDimension === BREAKDOWN_BY.NONE
			? CHART_TYPE_OPTIONS_WITHOUT_BREAKDOWN
			: CHART_TYPE_OPTIONS;
	};

	const getSelectedChartType = () => {
		return (
			getChartTypeOptions().find(item => item.value === chartType) ||
			DEFAULT_OPTION
		);
	};

	const onDownloadDataHandler = e => {
		if (e.value === 1) {
			history.push('/data/download');
		} else if (e.value === 2) {
			handleIndustryModal(true);
		} else {
			return;
		}
	};

	/**
	 * Restricting SASUser from using Donwload Deck
	 */
	const isSASUser = permissions.includes(PERMISSIONS.IntroductionRead);
	const DOWNLOAD_DATA_OPTIONS = useMemo(
		() => {
			const options = [{ label: 'Download Spend Cube', value: 1 }];
			if (isSASUser) {
				options.push({ label: 'Download Deck', value: 2 });
			}
			return options;
		},
		[isSASUser]
	);

	return (
		<ChartOptionsCollapsible header={breadcrumbs}>
			<Wrapper>
				<LeftWrapper>
					<Label>Show spend by</Label>
					<ApSimpleDropdown
						onChange={handleSpendByChange}
						options={spendByOptions}
						selected={getSelectedSpendBy()}
					/>
					{!hideBreakdown && (
						<React.Fragment>
							<Label>Break down by</Label>
							<ApSimpleDropdown
								onChange={handleBreakdownByChange}
								options={breakdownByOptions}
								selected={getSelectedBreakdownBy()}
							/>
						</React.Fragment>
					)}
				</LeftWrapper>
				<RightWrapper>
					<CheckboxWrapper>
						{!(spendType === '0') && (
							<ApCheckbox
								id="includeTailSpend"
								name="includeTailSpend"
								checked={includeTailSpend === 'true'}
								onChange={handleIncludeTailSpendChange}
							>
								Segregate Tail Spend
							</ApCheckbox>
						)}
					</CheckboxWrapper>
					<Divider />
					<ApSimpleDropdown
						onChange={onChartTypeChange}
						options={getChartTypeOptions()}
						selected={getSelectedChartType()}
					/>
					<Divider />
					<ApMenu button={<ApButtonIcon iconName="download" />}>
						{EXPORT_OPTIONS.map(option => (
							<ApMenuItem
								key={option.value}
								onClick={() => {
									if (option.value === 'png') {
										handleHighchartsExportToPng();
									} else if (option.value === 'xlsx') {
										handleExportToXls();
									}
								}}
							>
								{option.label}
							</ApMenuItem>
						))}
					</ApMenu>
					<Divider />
					<ApSimpleDropdown
						label="Download Data"
						options={DOWNLOAD_DATA_OPTIONS}
						onChange={onDownloadDataHandler}
					/>
				</RightWrapper>
				{isDownloadDeckModalOpen && (
					<DownloadDeckPopup
						handleDownloadDeck={handleDownloadDeck}
						handleIndustries={handleIndustries}
						onClose={() => handleIndustryModal(false)}
					/>
				)}
			</Wrapper>
		</ChartOptionsCollapsible>
	);
};

export default React.memo(props => {
	const {
		spendBy,
		splitSpendByDimension,
		includeTailSpend,
		setQueryStringStateAndPush,
		showNegativeSpend,
		tailSpendLevel,
		spendType,
	} = useContext(QueryStringContext);
	const { permissions } = useContext(PermissionsContext);
	return (
		<ChartOptions
			{...props}
			spendBy={spendBy}
			spendType={spendType}
			splitSpendByDimension={splitSpendByDimension}
			includeTailSpend={includeTailSpend}
			showNegativeSpend={showNegativeSpend}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
			isTailSpendOptions={tailSpendLevel != null}
			permissions={permissions}
		/>
	);
});
